import React, { useEffect, useState } from "react";
import { FileText, CheckCircle, ExternalLink, AlertCircle } from "lucide-react";
import { updateOnboardStatus, getAgreements } from "../../../services/coachAPI";

const DynamicAgreements = ({ onNext, onBack, coachData }) => {
  const fullName = `${coachData?.firstName || ""} ${coachData?.lastName || ""}`.trim();
  const coachId = coachData?.id || coachData?.uid || coachData?._id;

  const [agreements, setAgreements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAgreements = async () => {
      try {
        const data = await getAgreements();
        setAgreements(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("Failed to load agreements. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchAgreements();
  }, []);

  const currentAgreement = agreements[currentIndex];
  const isLast = currentIndex >= agreements.length - 1;
  const totalAgreements = agreements.length;
  const progressPercent =
    totalAgreements > 0 ? ((currentIndex + 1) / totalAgreements) * 100 : 0;

  const updateLocalUserData = (status) => {
    ["coachUser", "currentUser"].forEach((key) => {
      const stored = localStorage.getItem(key);

      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.onboardStatus = status;
        localStorage.setItem(key, JSON.stringify(parsed));
      }
    });
  };

  const handleSign = async (e) => {
    e.preventDefault();
    setError("");

    if (!signature.trim()) {
      setError("Please type your full name to sign this agreement.");
      return;
    }

    if (signature.trim().toLowerCase() !== fullName.toLowerCase()) {
      setError(
        `Name does not match. Please type your full legal name exactly as registered: ${fullName}.`
      );
      return;
    }

    if (!agreed) {
      setError("Please confirm that you have reviewed and agree to this agreement.");
      return;
    }

    setSaving(true);

    try {
      /**
       * Later you can call signAgreement API here for each agreement:
       *
       * await signAgreement(coachId, currentAgreement.id, {
       *   signatureName: signature.trim(),
       *   agreementTitle: currentAgreement.title,
       *   agreementVersion: currentAgreement.version,
       *   fileUrl: currentAgreement.fileUrl,
       * });
       */

      if (isLast) {
        await updateOnboardStatus(coachId, "phase_3_completed");
        updateLocalUserData("phase_3_completed");
        onNext();
      } else {
        setCurrentIndex((prev) => prev + 1);

        // Important: clear signature for next agreement
        setSignature("");
        setAgreed(false);
      }
    } catch (err) {
      setError(err.message || "Failed to save agreement.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error && agreements.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600">{error}</p>

        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl"
        >
          Retry
        </button>
      </div>
    );
  }

  if (agreements.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">No agreements to sign at this time.</p>

        <button
          onClick={onNext}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <div className="p-2 bg-blue-100 rounded-lg">
          <FileText className="h-6 w-6 text-blue-600" />
        </div>

        <div>
          <h2 className="text-xl font-bold text-gray-900">Agreements</h2>
          <p className="text-sm text-gray-500">
            Please review and sign each required agreement.
          </p>
        </div>
      </div>

      {/* Agreement Progress Bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">
            Agreement Progress
          </p>

          <p className="text-sm text-gray-500">
            {currentIndex + 1} of {totalAgreements}
          </p>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
          <div
            className="h-2.5 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Complete all agreement signatures to continue to the next onboarding step.
        </p>
      </div>

      {/* Current Agreement Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-500 mb-1">
              Agreement {currentIndex + 1}
            </p>

            <h3 className="text-lg font-semibold text-gray-900">
              {currentAgreement.title}
            </h3>

            {currentAgreement.isRequired && (
              <span className="inline-block mt-2 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">
                Required
              </span>
            )}
          </div>

          <a
            href={currentAgreement.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors font-medium text-sm flex-shrink-0"
          >
            <ExternalLink className="h-4 w-4" />
            View Document
          </a>
        </div>
      </div>

      {/* Important Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex gap-3">
        <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />

        <div className="text-sm text-yellow-800">
          <p className="font-semibold mb-1">Please read carefully before signing.</p>
          <p>
            Open and review the full agreement document before continuing. By typing your
            full legal name below, you confirm that you have read and accepted this
            agreement. Your typed name will be considered your digital signature for this
            agreement.
          </p>
        </div>
      </div>

      {/* Signature Form */}
      <form onSubmit={handleSign} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type your full legal name to sign *
          </label>

          <input
            type="text"
            required
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder={`Type exactly: ${fullName}`}
            disabled={saving}
            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />

          <p className="mt-1 text-xs text-gray-500">
            You must type your name again for each agreement. Registered name:{" "}
            <span className="font-medium text-gray-700">{fullName}</span>
          </p>
        </div>

        <label className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={saving}
            className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:cursor-not-allowed"
          />

          <span className="text-sm text-gray-700">
            I have opened, reviewed, and understood this agreement. I agree that
            typing my full legal name will be treated as my digital signature.
          </span>
        </label>

        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            Back
          </button>

          <button
            type="submit"
            disabled={saving || !signature.trim() || !agreed}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 shadow-lg transition-all"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              <CheckCircle className="h-5 w-5" />
            )}

            {saving ? "Saving..." : isLast ? "Sign & Complete" : "Sign & Next"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DynamicAgreements;